import type { ProInsightsProps } from "./types";

import { ProInsightsDataLine } from "./components/ProInsightsDataLine";
import { ProInsightsHeader } from "./components/ProInsightsHeader";
import { useBuildEmployerInsightsData } from "./utils/useBuildEmployerInsightsData";
import { Box, Clock, Divider, Flex } from "../../../../../mads";
import { DataBar } from "../../../../../mads/icons";
import { useI18n } from "../../../../../shared/I18n";
import { useViewjobOneGraphModel } from "../../../context/ViewJobDataContext";

import { colors, spacing } from "@indeed/ifl-tokens/react-native";
import React from "react";

const MIN_APPLICANTS_FOR_INSIGHTS = 2;

export function ProInsights(props: ProInsightsProps): React.JSX.Element | null {
  const { applicantInsightsModel, employerInsightsModel, datePublished } = props;
  const gt = useI18n();

  const totalCount = applicantInsightsModel?.count?.totalCount ?? -1;
  const hasApplicantData = totalCount !== -1;
  const hasInsights = hasApplicantData && totalCount >= MIN_APPLICANTS_FOR_INSIGHTS;

  const getApplicantCountHeading = (): string => {
    if (!hasApplicantData) {
      return gt.pgettext(
        "Pro insights empty state for non-Indeed jobs",
        "Application data not available",
      );
    }

    if (!hasInsights) {
      return gt.pgettext("Pro insights empty state for low applicants", "Be an early applicant");
    }

    return gt.npgettext(
      "Total applicants submitted on Indeed",
      "{0} application submitted on Indeed",
      "{0} applications submitted on Indeed",
      totalCount,
      totalCount.toString(),
    );
  };

  const getTodayCountDescription = (): string | undefined => {
    if (!hasInsights) {
      return undefined;
    }

    const last24hCount = applicantInsightsModel?.count?.last24hCount ?? 0;
    if (last24hCount === 0) {
      return undefined;
    }

    return gt.npgettext(
      "Number of people who applied today",
      "{0} person applied today",
      "{0} people applied today",
      last24hCount,
      last24hCount.toString(),
    );
  };

  const getJobAgeHeading = (): string | undefined => {
    if (!datePublished || datePublished <= 0) {
      return undefined;
    }

    const now = Date.now();
    const ageInMilliseconds = now - datePublished;
    const ageInHours = Math.floor(ageInMilliseconds / (1000 * 60 * 60));
    const ageInDays = Math.floor(ageInMilliseconds / (1000 * 60 * 60 * 24));

    if (ageInHours <= 0 || ageInHours === 1) {
      return gt.pgettext("Job age for jobs in the last hour", "Job posted 1 hour ago");
    }

    if (ageInDays === 0) {
      return gt.pgettext("Job age in hours", "Job posted {0} hours ago", ageInHours.toString());
    }

    if (ageInDays <= 30) {
      return gt.npgettext(
        "Job age in days",
        "Job posted {0} day ago",
        "Job posted {0} days ago",
        ageInDays,
        ageInDays.toString(),
      );
    }

    return gt.pgettext("Job age for old jobs", "Job posted over 30 days ago");
  };

  const applicantCountHeading = getApplicantCountHeading();
  const todayCountDescription = getTodayCountDescription();
  const employerInsightsData = useBuildEmployerInsightsData(employerInsightsModel);
  const jobAgeHeading = getJobAgeHeading();

  return (
    <Flex sx={{ flexDirection: "column", overflow: "visible" }}>
      <Box sx={{ overflow: "visible" }}>
        <ProInsightsHeader />
        <Box sx={{ marginBlockStart: spacing.s3 }}>
          <ProInsightsDataLine
            icon={DataBar}
            iconColor={colors.neutral1000}
            headingText={applicantCountHeading}
            descriptions={todayCountDescription ? [todayCountDescription] : []}
          />
          {employerInsightsData && (
            <ProInsightsDataLine
              icon={employerInsightsData.icon}
              iconColor={employerInsightsData.iconColor}
              headingText={employerInsightsData.headingText}
              descriptions={employerInsightsData.descriptions ?? []}
            />
          )}
          {jobAgeHeading && (
            <ProInsightsDataLine
              icon={Clock}
              iconColor={colors.neutral1000}
              headingText={jobAgeHeading}
              descriptions={[]}
            />
          )}
        </Box>
      </Box>
      <Divider
        sx={{
          marginTop: 32,
          marginBottom: 2,
        }}
      />
    </Flex>
  );
}

export function ProInsightsWithContext(props: { reload: () => void }): React.JSX.Element | null {
  const { reload } = props;
  const vjModel = useViewjobOneGraphModel();

  const applicantInsightsModel = vjModel?.applicantInsightsModel;
  const employerInsightsModel = vjModel?.employerInsightsModel;
  const datePublished = vjModel?.job.datePublished;

  const isProInsightsActive = vjModel?.features?.isJsProVjProInsightsTogActive ?? false;

  if (!isProInsightsActive || !employerInsightsModel) {
    return null;
  }

  return (
    <ProInsights
      applicantInsightsModel={applicantInsightsModel}
      employerInsightsModel={employerInsightsModel}
      datePublished={datePublished}
      reload={reload}
    />
  );
}
